import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Star, Sparkles, Package, Search, Download } from "lucide-react";
import { api, API } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";

const StorePage = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [category, setCategory] = useState("");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => { load(); }, [category]);
  useEffect(() => { api.get("/store/categories").then((r) => setCategories(r.data || [])); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/store/products", { params: category ? { category } : {} });
      setProducts(r.data || []);
    } catch (e) {}
    finally { setLoading(false); }
  };

  const filtered = products.filter((p) => !q.trim() || (p.name + " " + (p.short_desc || "")).toLowerCase().includes(q.toLowerCase()));

  return (
    <div data-testid="store-page" className="max-w-7xl mx-auto px-4 lg:px-8 py-10">
      <div className="mb-8">
        <div className="text-xs uppercase tracking-[0.14em] text-orange-600 font-semibold">AP Digital Store</div>
        <h1 className="mt-2 text-4xl sm:text-5xl font-heading font-semibold text-slate-900 tracking-tight">Premium digital tools & files</h1>
        <p className="mt-3 text-base text-slate-600 max-w-2xl">Software, CSC utilities, ready-to-fill forms, study material — instant download after purchase.</p>
      </div>

      <div className="flex flex-wrap gap-2 mb-6 items-center">
        <div className="flex-1 min-w-[200px] flex items-center gap-2 rounded-md bg-white border border-slate-200 px-3 py-2">
          <Search className="w-4 h-4 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search products…" className="border-0 focus-visible:ring-0 h-8" data-testid="store-search" />
        </div>
        <button onClick={() => setCategory("")} className={`px-3 py-1.5 rounded-full text-xs font-medium ${!category ? "bg-orange-600 text-white" : "bg-white border border-slate-200 text-slate-700"}`}>All</button>
        {categories.map((c) => (
          <button key={c} onClick={() => setCategory(c)} className={`px-3 py-1.5 rounded-full text-xs font-medium ${category === c ? "bg-orange-600 text-white" : "bg-white border border-slate-200 text-slate-700 hover:bg-slate-50"}`} data-testid={`cat-${c}`}>{c}</button>
        ))}
      </div>

      {loading ? <div className="text-center text-slate-500 py-16">Loading…</div> :
        filtered.length === 0 ? <div className="text-center text-slate-400 py-16 rounded-lg border-2 border-dashed border-slate-200 bg-white/50"><Package className="w-8 h-8 mx-auto mb-2" /> No products yet.</div> :
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
          {filtered.map((p) => {
          const hasOffer = false;
            return (
              <div key={p.id} onClick={() => navigate(`/store/${p.key}`)} className="group cursor-pointer flex flex-col rounded-xl bg-white border border-slate-200 overflow-hidden hover:-translate-y-1 hover:shadow-lg transition-all" data-testid={`product-card-${p.key}`}>
                <div className="h-44 bg-gradient-to-br from-slate-100 to-slate-200 relative">
                  {p.thumbnail_file ? <img src={`${API}/store/products/${p.key}/thumbnail`} alt={p.name} className="w-full h-full object-cover" /> :
                    <div className="w-full h-full flex items-center justify-center text-slate-400"><Package className="w-8 h-8" /></div>}
                  <div className="absolute top-2 left-2 flex gap-1 flex-wrap">
                    {p.highlight && <Badge className="bg-pink-500 text-white text-xs">⭐ Highlight</Badge>}
                    {p.featured && <Badge className="bg-orange-500 text-white text-xs"><Star className="w-3 h-3 mr-0.5" /> Featured</Badge>}
                    {p.is_new && <Badge className="bg-green-500 text-white text-xs"><Sparkles className="w-3 h-3 mr-0.5" /> New</Badge>}
                  </div>
                </div>
                <div className="p-4 flex-1 flex flex-col">
                  <div className="text-xs text-slate-500">{p.category}</div>
                  <h3 className="mt-1 font-heading font-semibold text-slate-900 line-clamp-2">{p.name}</h3>
                  <p className="mt-1 text-xs text-slate-600 line-clamp-2 flex-1">{p.short_desc}</p>
                  <div className="mt-3 flex gap-2">
                    <Button size="sm" className="flex-1 bg-slate-900 hover:bg-slate-700 text-white" data-testid={`view-${p.key}`}>विवरण देखें <ArrowRight className="w-3 h-3 ml-1" /></Button>
                    {p.has_file && (
                      <a href={`${API}/store/download-public/${p.key}`} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} className="inline-flex items-center justify-center h-9 px-3 rounded-md bg-green-600 hover:bg-green-700 text-white text-xs font-medium" data-testid={`dl-${p.key}`}><Download className="w-3 h-3 mr-1" /> Download</a>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      }
    </div>
  );
};

export default StorePage;
